import queryString from 'query-string-for-all';
import loadable from '@loadable/component';
import productCollectionPageQuery from './query.graphql';
import productCollectionSearchQuery from './search.graphql';

const ProductCollection = loadable(() =>
  import(/* webpackChunkName: "product-collection" */ './product-collection')
);

ProductCollection.graphQL = productCollectionPageQuery;

ProductCollection.loadOrder = [{ main: true }];

const mergeIntoQueryArray = (arr, obj, prop) => {
  const currentObj = arr.find(item => item[prop] === obj[prop]);
  if (currentObj) {
    return arr.map(item =>
      item[prop] === obj[prop]
        ? {
            ...item,
            ...obj,
          }
        : item
    );
  }
  return arr.concat(obj);
};

const fetches = {};

const refreshAggregation = (query, variables, action) => async ({
  dispatch,
  graphql,
}) => {
  // Avoid race conditions when data arrives in different order
  // by setting a current id
  const current = Math.random();
  fetches[variables.aggregationId] = current;

  try {
    if (action) {
      dispatch(action, { fetch: 'none' });
    }
    const res = await graphql(query, variables);

    if (res.errors) {
      return;
    }

    // A later fetch is running, do not merge data
    if (fetches[variables.aggregationId] !== current) {
      return;
    }

    dispatch(
      {
        type: 'AGGREGATION_RECEIVE',
        id: variables.aggregationId,
        data: res.data,
      },
      { dataReducer: true }
    );
  } catch (err) {
    console.error(err);
  }
};

const replaceOrAdd = (array, entry, matchBy = 'property') => {
  const index = array.findIndex(x => x[matchBy] === entry[matchBy]);
  const newArr = [...array];
  if (index >= 0) {
    newArr.splice(index, 1, entry);
  } else {
    newArr.push(entry);
  }

  return newArr;
};

ProductCollection.dataReducer = (action, data) => {
  switch (action.type) {
    case 'AGGREGATION_RECEIVE': {
      const { id } = action;
      const slice = data.productCollection.slices.find(x => x.id === 'filter');

      if (!slice) {
        return null;
      }

      if (!slice.sections.find(x => x.id === id)) {
        return null;
      }

      // There is a memo on the sections that we need to bail out from
      slice.sections = [].concat(
        slice.sections.map(section => {
          if (section.id !== id) {
            return section;
          }

          return {
            ...section,
            ...action.data.productCollectionAggregation,
          };
        })
      );

      return {
        ...data,
      };
    }
    default: {
      return data;
    }
  }
};

const onlyValidFilters = filter => {
  switch (filter.type) {
    case 'term':
      return filter.id;
    case 'terms':
      return filter.ids && filter.ids.length;
    default:
      return true;
  }
};

ProductCollection.reducer = (state = { aggregations: [] }, action) => {
  switch (action.type) {
    case 'SSR':
    case 'URL_CHANGE': {
      const { offset, sort, direction } = queryString.parse(
        action.location.search
      );
      return {
        ...state,
        offset: parseInt(offset, 10) || 0,
        sort,
        direction,
        // Use url to construct new filters server side
        url: action.location.pathname + action.location.search,
        filters: [],
      };
    }
    case 'ROUTE_DATA_REFETCHED': {
      return ({ history }) => {
        if (action.data?.productCollection?.url) {
          history.push(action.data.productCollection.url, {
            refreshRoute: false,
          });
        }
      };
    }
    case 'ROUTE_LOADED': {
      return {
        ...state,
        id: action.data?.productCollection?.id,
        // Clear url and use local filters instead
        url: null,
        filters: action.data?.productCollection?.filters,
      };
    }
    case 'FILTER_RANGE_UPDATE': {
      return {
        ...state,
        filters: replaceOrAdd(state.filters, action.filter),
      };
    }
    case 'FILTER_RANGE_GROUP_UPDATE': {
      const filter = {
        type: 'range-group',
        property: action.property,
        key: action.id,
      };

      return {
        ...state,
        filters: replaceOrAdd(state.filters, filter),
      };
    }
    case 'FILTER_BOOLEAN_UPDATE': {
      const filter = {
        type: 'boolean',
        id: action.id,
        property: action.property,
      };
      const newFilters = replaceOrAdd(state.filters, filter);
      return {
        ...state,
        filters: newFilters.filter(onlyValidFilters),
      };
    }
    case 'FILTER_LIST_UPDATE': {
      const { id, clearCurrentFilters } = action;
      const filters = clearCurrentFilters ? [] : state.filters;
      const currentFilter = filters.find(x => x.property === action.property);

      const ids = [].concat(currentFilter?.ids || [], currentFilter?.id || []);

      if (!id) {
        return state;
      }

      if (!action.multiple) {
        const newFilters = replaceOrAdd(filters, {
          type: 'term',
          id,
          property: action.property,
        });

        return {
          ...state,
          filters: newFilters.filter(onlyValidFilters),
        };
      }

      const index = ids.indexOf(id);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        ids.push(id);
      }

      const filter =
        ids.length === 1
          ? {
              type: 'term',
              property: action.property,
              id: ids[0],
            }
          : {
              type: 'terms',
              property: action.property,
              ids,
            };

      const newFilters = replaceOrAdd(filters, filter);

      return {
        ...state,
        filters: newFilters.filter(onlyValidFilters),
      };
    }
    case 'FILTER_LIST_TREE_UPDATE': {
      const otherActiveFilters = state.filters.filter(
        filter => filter.property !== action.property
      );
      const filters = action.ids.length
        ? [
            ...otherActiveFilters,
            {
              type: 'terms',
              property: action.property,
              ids: action.ids,
            },
          ]
        : otherActiveFilters;
      return {
        ...state,
        filters,
      };
    }
    case 'FILTER_REMOVE': {
      // If a property is passed, clear filters for that property, otherwise all filters
      return action.property
        ? {
            ...state,
            filters: state.filters.filter(
              filter => filter.property !== action.property
            ),
          }
        : { ...state, filters: [] };
    }
    case 'AGGREGATION_CHANGED': {
      return {
        ...state,
        aggregations: mergeIntoQueryArray(
          state.aggregations,
          action.aggregation,
          'id'
        ),
      };
    }
    case 'AGGREGATION_SEARCH':
    case 'AGGREGATION_MORE_ITEMS': {
      const newAggregation = {
        ...state.aggregations.find(agg => agg.id === action.id),
        id: action.id,
      };

      if (action.query !== undefined) {
        newAggregation.size = 15;
        newAggregation.query = action.query;
      }

      if (action.size) {
        newAggregation.size = action.size;
      }

      const variables = {
        id: state.id,
        aggregationId: action.id,
        query: {
          filters: state.filters,
          aggregations: [newAggregation],
        },
      };

      return refreshAggregation(productCollectionSearchQuery, variables, {
        type: 'AGGREGATION_CHANGED',
        aggregation: newAggregation,
      });
    }
    default: {
      return state;
    }
  }
};

ProductCollection.variables = ({ id, slug }) => ({
  id,
  slug,
});

ProductCollection.stateToVariables = state => ({
  query: {
    url: state.url,
    filters: !state.url && state.filters ? state.filters : undefined,
    aggregations: state.aggregations,
    sort: state.sort,
    direction: state.direction,
    offset: state.offset,
  },
});

export default ProductCollection;
